import { formatFixedPrice } from '../../utils/utils'
import { getMealPriceByBox } from 'src/utils/menu'
import { Meal } from 'src/types/meal'
import { ReactComponent as PremiumIcon } from '../../assets/svg/mealcard/premium-meals.svg'
import { ReactElement } from 'react'
import { MembershipMainTagsText } from 'src/hooks/useMembershipMainTagsText'

interface GetAddButtonTextParams {
  meal: Meal
  planIsCompleted: boolean
  isExtra: boolean
  isFlexUser: boolean
  noExtraMealPlanIsCompleted: boolean
  currentBOMBox: any
  showPremiumFee: boolean
  useBOMMultiplePrices?: boolean
  isUnityPassActive?: boolean
}

interface GetCondensedPriceTextParams {
  meal: Meal
  planIsCompleted: boolean
  isExtra: boolean
  isFlexUser: boolean
  noExtraMealPlanIsCompleted: boolean
  currentBOMBox: any
  useBOMMultiplePrices?: boolean
  showPremiumFee?: boolean
  isUnityPassActive?: boolean
}

const formatPriceText = {
  addMeal: (price: number) => `Add meal for ${formatFixedPrice(price)}`,
  addExtra: (price: number) => `Add extra for ${formatFixedPrice(price)}`,
  addFor: (price: number) => `Add for ${formatFixedPrice(price)}`,
  addMealPlusFee: (fee: number) => `Add meal + ${formatFixedPrice(fee)}`
}

interface CommonPriceLogicParams {
  meal: Meal
  planIsCompleted: boolean
  isExtra: boolean
  isFlexUser: boolean
  noExtraMealPlanIsCompleted: boolean
  currentBOMBox: any
  useBOMMultiplePrices?: boolean
  isUnityPassActive?: boolean
  showPremiumFee?: boolean
  isCondensed?: boolean
}

const handleBOMBoxPrice = (
  meal: Meal,
  currentBOMBox: any,
  finalPrice: number,
  useBOMMultiplePrices?: boolean
) => {
  let mealPrice = finalPrice
  if (useBOMMultiplePrices && meal.prices?.length) {
    const boxPrice = getMealPriceByBox(meal.prices, currentBOMBox)
    mealPrice = boxPrice?.finalPrice ?? finalPrice
  }
  return mealPrice
}

const getPriceText = ({
  meal,
  planIsCompleted,
  isExtra,
  isFlexUser,
  noExtraMealPlanIsCompleted,
  currentBOMBox,
  useBOMMultiplePrices = false,
  isUnityPassActive = false,
  showPremiumFee = false,
  isCondensed = false
}: CommonPriceLogicParams) => {
  const finalPrice = meal.finalPrice ?? meal.price + (meal.premium_fee ?? 0)

  switch (true) {
    case !!meal.isFreePremiumMeal:
      return isCondensed ? '' : 'Select meal'
    case !!currentBOMBox:
      const mealPrice = handleBOMBoxPrice(
        meal,
        currentBOMBox,
        finalPrice,
        useBOMMultiplePrices
      )
      return formatPriceText.addFor(mealPrice)
    case noExtraMealPlanIsCompleted && !isExtra:
      return isCondensed ? '' : 'Add meal'
    case isUnityPassActive &&
      !meal.premium_special &&
      (meal.premium_fee ?? 0) > 0 &&
      !planIsCompleted:
      return formatPriceText.addMealPlusFee(meal.premium_fee ?? 0)
    case isFlexUser && isExtra:
      return formatPriceText.addFor(finalPrice)
    case isExtra:
      return formatPriceText.addExtra(finalPrice)
    case planIsCompleted:
      return formatPriceText.addMeal(finalPrice)
    case isFlexUser && (meal.premium_fee ?? 0) > 0:
      return formatPriceText.addFor(finalPrice)
    case (meal.premium_fee ?? 0) > 0:
      const defaultText = isCondensed ? '' : 'Add meal'
      return showPremiumFee
        ? formatPriceText.addMealPlusFee(meal.premium_fee ?? 0)
        : defaultText
    case isFlexUser:
      return formatPriceText.addMeal(finalPrice)
    default:
      return isCondensed ? '' : 'Add meal'
  }
}

export const getAddButtonText = (params: GetAddButtonTextParams) => {
  return getPriceText({ ...params, isCondensed: false })
}

export const getCondensedPriceText = (params: GetCondensedPriceTextParams) => {
  return getPriceText({ ...params, isCondensed: true })
}

const showProteinOnlyTag = (meal: Meal) =>
  meal.sidesSubCategoryNames?.includes('Protein')

const isBackInTheMenu = (meal: Meal) => !!meal.isBackInTheMenu
const isRestaurantExclusive = (meal: Meal) => !!meal.isMealRestaurantExclusive
const isMealWithRecipe = (meal: Meal) => !!meal.hasRecipe
export const isUnityPass = (meal: Meal) => !!meal.isUnityPass

const TAG_TYPES = {
  MERCHANDISE: ['Merchandising Labels', 'merchandising-labels']
}

export const MAIN_TAGS = {
  NEW: {
    label: 'NEW',
    color: 'yellow',
    weight: 'bold'
  },
  PREMIUM: {
    label: 'Premium',
    color: 'dark',
    icon: <PremiumIcon />,
    weight: 'bold'
  },
  ITS_BACK: {
    label: "IT'S BACK!",
    color: 'dark',
    weight: 'bold'
  },
  RESTAURANT_EXCLUSIVE: {
    label: 'Restaurant Exclusive',
    color: 'dark',
    weight: 'bold'
  },
  INCLUDES_RECIPE: {
    label: 'Includes Recipe',
    color: 'dark',
    weight: 'bold'
  },
  CLIMATE_FRIENDLY: {
    label: 'Climate Friendly',
    color: 'dark',
    weight: 'bold'
  },
  PROTEIN_ONLY: {
    label: 'Protein Only',
    color: 'dark',
    weight: 'bold'
  },
  UNITY_PASS: {
    label: '<strong>unity</strong>pass',
    color: 'gold',
    weight: 'normal'
  },
  UNITYPASS_EXCLUSIVE_SKU: {
    label: 'Unlock with UnityPass',
    color: 'dark',
    weight: 'bold'
  }
}

export const isNew = (meal: Meal) => meal.isNewMeal

interface ExclusiveSKUData {
  isExclusiveMembershipProduct: boolean
  unlockTagText: string
  includedTagText: string
}

interface GetMainTagsParams {
  meal: Meal
  max: number
  backInTheMenuEnabled: boolean
  recipesEnabled: boolean
  menuPromotion?: {
    asPromotion?: boolean
    type: string
    formattedAmount: string
    formattedRibbon: string
    tooltipText: string
    categoryId: number
    subCategoryId: number
    icon: ReactElement
  }
  exclusiveSKUData?: ExclusiveSKUData
  isUnityPassActive?: boolean
  membershipDiscountCopy?: MembershipMainTagsText | null
}

export const getMainTags = ({
  meal,
  max,
  backInTheMenuEnabled,
  recipesEnabled,
  menuPromotion,
  exclusiveSKUData = {
    isExclusiveMembershipProduct: false,
    unlockTagText: '',
    includedTagText: ''
  },
  isUnityPassActive = false,
  membershipDiscountCopy = null
}: GetMainTagsParams) => {
  const result: typeof MAIN_TAGS[keyof typeof MAIN_TAGS][] = []
  const label = isUnityPassActive
    ? exclusiveSKUData.includedTagText
    : exclusiveSKUData.unlockTagText
  if (exclusiveSKUData.isExclusiveMembershipProduct && label) {
    result.push({
      ...MAIN_TAGS.UNITYPASS_EXCLUSIVE_SKU,
      label
    })
  }

  const showIsBackInTheMenu = backInTheMenuEnabled && isBackInTheMenu(meal)
  if (isNew(meal) && !showIsBackInTheMenu) {
    result.push(MAIN_TAGS.NEW)
  }
  if (showIsBackInTheMenu) {
    result.push(MAIN_TAGS.ITS_BACK)
  }
  if (meal.premium_special) {
    result.push(MAIN_TAGS.PREMIUM)
  }
  if (showProteinOnlyTag(meal)) {
    result.push(MAIN_TAGS.PROTEIN_ONLY)
  }

  if (
    meal.membershipAddAndSaveDiscount &&
    membershipDiscountCopy?.addAndSaveCopy
  ) {
    result.push({
      ...MAIN_TAGS.UNITY_PASS,
      label: `<b>$${meal.membershipAddAndSaveDiscount} ${membershipDiscountCopy?.addAndSaveCopy}</b>`
    })
  }

  if (meal.membershipPremiumDiscount && membershipDiscountCopy?.premiumCopy) {
    result.push({
      ...MAIN_TAGS.UNITY_PASS,
      label: `<b>$${meal.membershipPremiumDiscount} ${membershipDiscountCopy.premiumCopy}</b>`
    })
  }

  if (!!menuPromotion && !!menuPromotion.formattedAmount) {
    result.push({
      label: menuPromotion.formattedAmount,
      color: 'blue',
      icon: menuPromotion.icon,
      weight: ''
    })
  }
  if (meal.all_tags) {
    const merchandiseTags = meal.all_tags
      ?.filter(tag => TAG_TYPES.MERCHANDISE.includes(tag.type))
      .filter(
        tag => tag.name !== 'Climate-Friendly' && tag.name !== 'Low Emissions'
      )
    merchandiseTags.forEach(tag => {
      result.push({
        label: tag.name,
        color: 'dark',
        weight: ''
      })
    })
  }
  if (recipesEnabled && isMealWithRecipe(meal)) {
    result.push(MAIN_TAGS.INCLUDES_RECIPE)
  }
  if (isRestaurantExclusive(meal)) {
    result.push(MAIN_TAGS.RESTAURANT_EXCLUSIVE)
  }

  if (isUnityPass(meal)) {
    result.push(MAIN_TAGS.UNITY_PASS)
  }

  return result?.length > max ? result.splice(0, max) : result
}
